import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

const IMG = {
  hero: 'https://cdn.shopify.com/s/files/1/0114/3547/5010/files/rotary-screen-printing-press-780x400_600x600.jpg?v=1639440552',
  // Rotary engraving — man beside green textile rotary printing machine
  rotogravure: 'https://images.unsplash.com/photo-1584269613077-1e64a0ac2779?auto=format&fit=crop&w=1600&q=80',
  // Glass screen printing — architectural facade
  glass: 'https://images.unsplash.com/photo-1481026469463-66327c86e544?auto=format&fit=crop&w=1600&q=80',
  // Technology — close-up of patterned printing cylinder with ink
  technology: 'https://images.unsplash.com/photo-1699662585180-8cd17dfa9195?auto=format&fit=crop&w=1600&q=80',
  // Silk screen frame held to the light — textile printing frame
  frame: 'https://images.unsplash.com/photo-1456456496250-d5e7c0a9b44d?auto=format&fit=crop&w=1600&q=80',
  sustain: 'https://images.unsplash.com/photo-1647427060118-4911c9821b82?auto=format&fit=crop&w=1800&q=80',
  factory: 'https://images.unsplash.com/photo-1593106410288-caf65eca7c9d?auto=format&fit=crop&w=1800&q=80',
  intl: 'https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?auto=format&fit=crop&w=1800&q=80',
};

export default function Home() {
  const { t } = useI18n();

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[92vh] w-full flex items-end overflow-hidden" data-testid="hero">
        <img
          src={IMG.hero}
          alt="Rotary screen printing press"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest/65 to-forest/15" />
        <div className="absolute inset-0 grain" />

        <div className="container-x relative z-10 pb-16 md:pb-24 pt-32 text-sand">
          <Reveal delay={0} className="kicker !text-rust">
            {t.home.kicker}
          </Reveal>
          <Reveal delay={80}>
            <h1 className="display-1 text-sand mt-6 max-w-[18ch] text-balance">
              {t.home.heroTitle}
            </h1>
          </Reveal>
          <Reveal delay={180} className="mt-8 max-w-2xl lead !text-sand/85">
            {t.home.heroSub}
          </Reveal>
          <Reveal delay={260} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/servicios" className="btn-primary !bg-sand !text-forest hover:!bg-rust hover:!text-sand" data-testid="hero-cta-services">
              {t.cta.ourServices} <ArrowRight size={14} />
            </Link>
            <Link to="/contacto" className="btn-ghost-dark" data-testid="hero-cta-contact">
              {t.cta.contact} <ArrowUpRight size={14} />
            </Link>
          </Reveal>

          <div className="absolute right-6 md:right-12 bottom-12 hidden md:flex items-center gap-3 text-sand/65 text-[10px] uppercase tracking-[0.25em]">
            <span className="h-px w-10 bg-sand/40" />
            {t.home.heroLine1}
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="container-x py-20 md:py-28" data-testid="stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 md:gap-y-0">
          {t.home.stats.map((s, i) => (
            <Reveal
              key={i}
              delay={i * 80}
              className={`flex flex-col gap-2 md:px-8 ${i !== 0 ? 'md:border-l border-forest/15' : ''}`}
            >
              <span className="font-serif text-5xl md:text-6xl font-light tracking-tight text-forest">
                {s.num}
              </span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-forest/55 mt-2">
                {s.label}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ INTRO 50/50 ============ */}
      <section className="bg-sand-warm" data-testid="intro">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-5">
            <div className="kicker">{t.home.introKicker}</div>
            <h2 className="display-2 mt-6 text-forest text-balance">{t.home.introTitle}</h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6 md:col-start-7 space-y-6">
            <p className="lead">{t.home.introBody}</p>
            <Link to="/empresa" className="inline-flex items-center gap-2 text-forest link-underline text-[12px] uppercase tracking-[0.22em]" data-testid="intro-cta-about">
              {t.cta.learnMore} <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ SERVICES BENTO ============ */}
      <section className="container-x py-24 md:py-32" data-testid="services-teaser">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-14">
          <div className="md:col-span-7">
            <div className="kicker">{t.home.servicesKicker}</div>
            <h2 className="display-2 mt-6 text-forest text-balance">{t.home.servicesTitle}</h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="lead">{t.home.servicesIntro}</p>
            <Link to="/servicios" className="mt-6 inline-flex items-center gap-2 text-forest link-underline text-[12px] uppercase tracking-[0.22em]" data-testid="services-cta-all">
              {t.cta.viewAll} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 grid-rows-[auto] gap-4 md:gap-6">
          {/* Big image card */}
          <Reveal className="md:col-span-7 md:row-span-2 relative overflow-hidden group">
            <img src={IMG.rotogravure} alt={t.services.items[1].title} className="w-full h-full object-cover aspect-[4/3] md:aspect-auto md:h-[640px] transition-transform duration-700 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-forest/0" />
            <div className="absolute left-8 bottom-8 right-8 text-sand">
              <span className="label-num !text-sand/60">{t.services.items[1].n}</span>
              <h3 className="font-serif text-3xl md:text-4xl font-light mt-2">{t.services.items[1].title}</h3>
              <p className="mt-3 text-sand/80 max-w-md text-sm">{t.services.items[1].body}</p>
            </div>
          </Reveal>
          {/* Text-heavy card */}
          <Reveal delay={80} className="md:col-span-5 bg-forest text-sand p-10 md:p-12 flex flex-col justify-between min-h-[310px]">
            <div>
              <span className="label-num !text-sand/55">{t.services.items[0].n}</span>
              <h3 className="font-serif text-3xl font-light mt-3">{t.services.items[0].title}</h3>
            </div>
            <p className="text-sand/75 text-sm leading-relaxed">{t.services.items[0].body}</p>
          </Reveal>
          {/* Image card */}
          <Reveal delay={140} className="md:col-span-5 relative overflow-hidden group min-h-[310px]">
            <img src={IMG.glass} alt={t.services.items[5].title} className="w-full h-full object-cover absolute inset-0 transition-transform duration-700 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/85 to-forest/10" />
            <div className="absolute left-8 bottom-8 right-8 text-sand">
              <span className="label-num !text-sand/60">{t.services.items[5].n}</span>
              <h3 className="font-serif text-2xl md:text-3xl font-light mt-2">{t.services.items[5].title}</h3>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ TECH MARQUEE ============ */}
      <section className="border-y border-forest/15 overflow-hidden py-10 md:py-12" data-testid="tech-marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {['Laser LED', 'Inkjet', '3D Mapping', 'Rotativa', 'Plana 4000×7000', 'Separación', 'Sublimación', 'Flexografía'].map((label, i) => (
                <React.Fragment key={`${dup}-${i}`}>
                  <span className="font-serif text-[64px] md:text-[96px] font-light text-forest/15 leading-none px-12">
                    {label}
                  </span>
                  <span className="text-rust text-[36px] md:text-[48px]">·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ============ TECHNOLOGY SECTION ============ */}
      <section className="container-x py-24 md:py-32" data-testid="technology">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <div className="kicker">{t.home.techKicker}</div>
            <h2 className="display-2 mt-6 text-forest text-balance">{t.home.techTitle}</h2>
            <p className="lead mt-8">{t.home.techBody}</p>
            <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6">
              {[
                ['LASER', t.services.items[1].title],
                ['LED', t.services.items[5].title],
                ['INKJET', t.services.items[2].title],
                ['3D', t.services.items[4].title],
              ].map(([k, v], i) => (
                <div key={i} className="border-t border-forest/15 pt-4">
                  <div className="kicker !text-forest/55">{k}</div>
                  <div className="mt-2 font-serif text-lg text-forest">{v}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100} className="md:col-span-6 order-1 md:order-2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMG.frame} alt="Textile printing frame held to the light" className="w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-forest/10" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ INTERNATIONAL ============ */}
      <section className="relative" data-testid="international-teaser">
        <div className="absolute inset-0">
          <img src={IMG.intl} alt="World industrial reach" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/85" />
        </div>
        <div className="container-x relative py-24 md:py-32 text-sand">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-7">
              <div className="kicker !text-rust">{t.home.intlKicker}</div>
              <h2 className="display-2 mt-6 text-sand text-balance">{t.home.intlTitle}</h2>
            </Reveal>
            <Reveal delay={120} className="md:col-span-4 md:col-start-9 flex flex-col justify-end gap-6">
              <p className="text-sand/80 leading-relaxed">{t.home.intlBody}</p>
              <Link to="/internacional" className="inline-flex items-center gap-2 text-sand link-underline text-[12px] uppercase tracking-[0.22em]" data-testid="intl-cta">
                {t.cta.explore} <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-y-0">
            {t.international.regions.map((r, i) => (
              <Reveal key={i} delay={i * 70} className={`md:px-8 ${i !== 0 ? 'md:border-l border-sand/15' : ''}`}>
                <div className="kicker !text-rust">{String(i + 1).padStart(2, '0')}</div>
                <div className="font-serif text-2xl md:text-3xl font-light mt-3 text-sand">{r.name}</div>
                <div className="text-sand/65 text-sm mt-3">{r.body}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SUSTAINABILITY ============ */}
      <section className="container-x py-24 md:py-32" data-testid="sustainability-teaser">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <Reveal className="md:col-span-7 relative overflow-hidden">
            <img src={IMG.sustain} alt="Circular economy" className="w-full h-[420px] md:h-[560px] object-cover" />
          </Reveal>
          <Reveal delay={120} className="md:col-span-5 flex flex-col justify-center">
            <div className="kicker">{t.home.sustainKicker}</div>
            <h2 className="display-2 mt-6 text-forest text-balance">{t.home.sustainTitle}</h2>
            <p className="lead mt-8">{t.home.sustainBody}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <span className="px-4 py-2 border border-forest/30 text-[11px] uppercase tracking-[0.2em] text-forest">TAKE BACK RECILPACK</span>
              <span className="px-4 py-2 border border-forest/30 text-[11px] uppercase tracking-[0.2em] text-forest">TAKE BACK SCREEN FLAT</span>
            </div>
            <Link to="/idi" className="mt-8 inline-flex items-center gap-2 text-forest link-underline text-[12px] uppercase tracking-[0.22em]" data-testid="sustain-cta">
              {t.cta.learnMore} <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-forest text-sand" data-testid="final-cta">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <div className="kicker !text-rust">{t.home.finalCtaKicker}</div>
            <h2 className="display-2 mt-6 text-sand text-balance max-w-[20ch]">{t.home.finalCtaTitle}</h2>
            <p className="lead mt-8 !text-sand/75 max-w-2xl">{t.home.finalCtaBody}</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-4 flex md:justify-end">
            <Link to="/contacto" className="btn-primary !bg-rust hover:!bg-sand hover:!text-forest" data-testid="final-cta-button">
              {t.cta.getInTouch} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
